import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import TextareaAutosize from "react-textarea-autosize";
import { marked } from "marked";
import {
  Bot,
  User,
  Maximize2,
  Minimize2,
  X,
  MessageCircle,
  Square,
} from "lucide-react";
import SpeechToTextButton from "../CommonComponents/SpeechToText";
import fetchMethodRequest from "../../../config/service";
import { setChatbotState } from "../../../redux/actions/chatbotAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrophone, faStopCircle } from "@fortawesome/free-solid-svg-icons";
import "./Chatbot.css";

const Chatbot = () => {
  const dispatch = useDispatch();

  const {
    showChatbot,
    isOpen,
    conversation,
    inputValue,
    loading,

    timerEnabled,
    timeLeft,
    lastActivity,

    currentBotText,
    position,
    listening,
    fullscreenMode,
    isTypingStopped,
  } = useSelector((state) => state.chatbot);

  const isSidebarCollapsed = useSelector((state) => state.sidebar.collapse);

  /* ================= REFS ================= */
  const messagesEndRef = useRef(null);
  const typewriterRef = useRef(null);
  const didDrag = useRef(false);
  const inputRef = useRef(null);
  const transcriptRef = useRef("");

  const inactivityIntervalRef = useRef(null);

  const speechRef = useRef(null);
  const sessionIdRef = useRef(null);

  const repeatIntervalRef = useRef(null);
  const repeatedTextRef = useRef("");

  const selectedVoiceRef = useRef(null);
  const fullBotTextRef = useRef("");

  // Track which bot placeholder is currently active (so only it gets updated)
  const activeBotMsgIdRef = useRef(null);

  // Prevent Date.now() collisions
  const seqRef = useRef(0);
  const nextId = useCallback(() => `${Date.now()}_${++seqRef.current}`, []);

  /* ================= UTILS ================= */
  const scrollBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const stopSpeak = useCallback(() => {
    window.speechSynthesis.cancel();
    speechRef.current = null;
  }, []);

  const cleanTextForSpeech = useCallback((text = "") => {
    return text
      .replace(/[*_`#>-]/g, " ")
      .replace(/•/g, " ")
      .replace(/https?:\/\/\S+/g, "")
      .replace(/\[(.*?)\]|\((.*?)\)/g, "$1")
      .replace(/[^a-zA-Z.,!? ]/g, " ") // numbers removed here
      .replace(/\s+/g, " ")
      .trim();
  }, []);


  const speak = useCallback(
    (text) => {
      window.speechSynthesis.cancel();

      const cleanedText = cleanTextForSpeech(text);
      if (!cleanedText || !selectedVoiceRef.current) return;

      const utterance = new SpeechSynthesisUtterance(cleanedText);
      utterance.rate = 1;
      utterance.voice = selectedVoiceRef.current;

      utterance.onend = () => {
        speechRef.current = null;
        window.speechSynthesis.cancel();
      };

      utterance.onerror = () => {
        speechRef.current = null;
        window.speechSynthesis.cancel();
      };

      speechRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    },
    [cleanTextForSpeech]
  );

  const resetInactivity = useCallback(() => {
    if (!timerEnabled) return;
    dispatch(
      setChatbotState({
        lastActivity: Date.now(),
        timeLeft: 60,
      })
    );
  }, [dispatch, timerEnabled]);

  /* ================= KEEP IN VISIBLE RANGE ON RESIZE ================= */
  useEffect(() => {
    const handleResize = () => {
      if (fullscreenMode) return;

      const padding = 10;
      const elemWidth = isOpen ? 360 : 60;
      const elemHeight = isOpen ? 520 : 60;

      const maxX = window.innerWidth - elemWidth - padding;
      const maxY = window.innerHeight - elemHeight - padding;

      const newX = Math.min(Math.max(position.x, padding), maxX);
      const newY = Math.min(Math.max(position.y, padding), maxY);

      if (newX !== position.x || newY !== position.y) {
        dispatch(setChatbotState({ position: { x: newX, y: newY } }));
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [position, isOpen, fullscreenMode, dispatch]);

  /* ================= VOICES ================= */
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      if (!voices.length) return;

      const remoteVoices = voices.filter((v) => !v.localService);
      const novaVoice = voices.find(
        (v) =>
          v.name.toLowerCase().includes("nova") ||
          v.name.toLowerCase().includes("samantha")
      );

      selectedVoiceRef.current =
        novaVoice ||
        remoteVoices.find((v) => v.name.toLowerCase().includes("female")) ||
        remoteVoices.find((v) => v.lang.startsWith("en-")) ||
        voices[0];
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    dispatch(
      setChatbotState({
        timeLeft: 60,
        lastActivity: Date.now(),
      })
    );
  }, [isOpen, dispatch]);

  /* ================= TYPE/REPEAT EFFECTS ================= */
  const startRepeatingText = useCallback(
    (text) => {
      if (repeatIntervalRef.current) clearInterval(repeatIntervalRef.current);

      repeatedTextRef.current = text;
      let repeatCount = 0;
      const maxRepeats = 3;

      dispatch(setChatbotState({ currentBotText: text }));

      repeatIntervalRef.current = setInterval(() => {
        repeatCount++;
        dispatch(
          setChatbotState({
            currentBotText:
              repeatCount >= maxRepeats
                ? "Waiting for response..."
                : repeatedTextRef.current,
          })
        );
        scrollBottom();
      }, 2000);
    },
    [dispatch, scrollBottom]
  );

  const startTypewriter = useCallback(
    (text, onDone) => {
      if (repeatIntervalRef.current) {
        clearInterval(repeatIntervalRef.current);
        repeatIntervalRef.current = null;
      }

      if (typewriterRef.current) clearInterval(typewriterRef.current);

      let i = 0;
      dispatch(setChatbotState({ currentBotText: "" }));

      typewriterRef.current = setInterval(() => {
        if (i < text.length) {
          dispatch(setChatbotState({ currentBotText: text.slice(0, i + 1) }));
          i++;
          scrollBottom();
        } else {
          clearInterval(typewriterRef.current);
          typewriterRef.current = null;
          onDone?.();
        }
      }, 20);
    },
    [dispatch, scrollBottom]
  );

  /* ================= GRAPH RENDERER ================= */
  const renderGraph = useCallback((graphData) => {
    if (!graphData) return null;

    try {
      const graphImage = graphData?.graph || graphData;

      return (
        <div className="graph-container">
          <div className="graph-header">
            <h4>Graph Visualization</h4>
          </div>

          <img
            src={`data:image/png;base64,${graphImage}`}
            alt="Graph Visualization"
            className="graph-image"
            style={{ cursor: "pointer" }}
          />
        </div>
      );
    } catch (error) {
      console.error("Error rendering graph:", error);
      return (
        <div className="graph-container">
          <div className="graph-placeholder">
            <p>⚠️ Could not render graph</p>
          </div>
        </div>
      );
    }
  }, []);

  /* ================= CLOSE CHAT ================= */
  const closeChat = useCallback(() => {
    stopSpeak();

    if (typewriterRef.current) {
      clearInterval(typewriterRef.current);
      typewriterRef.current = null;
    }
    if (repeatIntervalRef.current) {
      clearInterval(repeatIntervalRef.current);
      repeatIntervalRef.current = null;
    }
    if (inactivityIntervalRef.current) {
      clearInterval(inactivityIntervalRef.current);
      inactivityIntervalRef.current = null;
    }

    activeBotMsgIdRef.current = null;
    fullBotTextRef.current = "";

    // IMPORTANT: Do NOT clear conversation here
    dispatch(
      setChatbotState({
        isOpen: false,
        fullscreenMode: false,
        listening: false,
        currentBotText: "",
        // optional: keep draft too by removing next line
        inputValue: "",
        timeLeft: 60,
        lastActivity: Date.now(),
        isTypingStopped: false,
      })
    );
  }, [dispatch, stopSpeak]);

  // const closeChat = useCallback(() => {
  //   stopSpeak();

  //   if (typewriterRef.current) {
  //     clearInterval(typewriterRef.current);
  //     typewriterRef.current = null;
  //   }
  //   if (repeatIntervalRef.current) {
  //     clearInterval(repeatIntervalRef.current);
  //     repeatIntervalRef.current = null;
  //   }
  //   if (inactivityIntervalRef.current) {
  //     clearInterval(inactivityIntervalRef.current);
  //     inactivityIntervalRef.current = null;
  //   }

  //   activeBotMsgIdRef.current = null;
  //   fullBotTextRef.current = "";

  //   dispatch(
  //     setChatbotState({
  //       isOpen: false,
  //       fullscreenMode: false,
  //       conversation: [],
  //       inputValue: "",
  //       listening: false,
  //       currentBotText: "",
  //       timeLeft: 60,
  //       lastActivity: Date.now(),
  //       isTypingStopped: false,
  //     })
  //   );
  // }, [dispatch, stopSpeak]);


  /* ================= INACTIVITY TIMER ================= */
  useEffect(() => {
    if (!isOpen || !timerEnabled || loading) return;

    if (inactivityIntervalRef.current) {
      clearInterval(inactivityIntervalRef.current);
      inactivityIntervalRef.current = null;
    }

    inactivityIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastActivity) / 1000);
      const remaining = 60 - elapsed;

      if (remaining <= 0) {
        closeChat();
        return;
      }

      dispatch(setChatbotState({ timeLeft: remaining }));
    }, 1000);

    return () => {
      if (inactivityIntervalRef.current) {
        clearInterval(inactivityIntervalRef.current);
        inactivityIntervalRef.current = null;
      }
    };
  }, [isOpen, timerEnabled, lastActivity, dispatch, closeChat, loading]);

  /* ================= INITIAL WELCOME ================= */
  useEffect(() => {
    if (isOpen && conversation.length === 0) {
      const welcome = "Welcome to Lexato Support. How can I help you?";
      dispatch({
        type: "CHAT_APPEND_MESSAGES",
        data: [{ id: nextId(), sender: "bot", text: welcome }],
      });
      speak(welcome);
      resetInactivity();
    }
  }, [isOpen, conversation.length, dispatch, speak, resetInactivity, nextId]);

  /* ================= SEND MESSAGE ================= */
  const sendMessage = useCallback(async (messageOverride) => {
    const outgoingText = (typeof messageOverride === "string" ? messageOverride : inputValue).trim();
    if (!outgoingText) return;

    dispatch(setChatbotState({ isTypingStopped: false }));
    resetInactivity();
    stopSpeak();

    // Stop previous visual typing loops before starting a new one
    if (typewriterRef.current) {
      clearInterval(typewriterRef.current);
      typewriterRef.current = null;
    }
    if (repeatIntervalRef.current) {
      clearInterval(repeatIntervalRef.current);
      repeatIntervalRef.current = null;
    }

    fullBotTextRef.current = "";

    const userMsg = { id: nextId(), sender: "user", text: outgoingText };
    const botId = nextId();
    const botMsg = { id: botId, sender: "bot", text: "", loading: true };

    activeBotMsgIdRef.current = botId;

    dispatch({ type: "CHAT_APPEND_MESSAGES", data: [userMsg, botMsg] });
    dispatch(setChatbotState({ inputValue: "", loading: true }));

    if (listening) dispatch(setChatbotState({ listening: false }));

    startRepeatingText("Thinking...");

    try {
      if (!sessionIdRef.current) sessionIdRef.current = `session_${Date.now()}`;

      const formData = new FormData();
      formData.append("query", userMsg.text);
      formData.append("sessionId", sessionIdRef.current);

      const response = await fetchMethodRequest(
        "POST",
        `filesAnalysis/dmsVoice?type=text&session=${sessionIdRef.current}`,
        formData
      );

      if (repeatIntervalRef.current) {
        clearInterval(repeatIntervalRef.current);
        repeatIntervalRef.current = null;
      }

      const botText = response?.ai_reply
        ? response.ai_reply
        : response?.graph
          ? "Here is the graph generated"
          : "I'm here to help.";

      fullBotTextRef.current = botText;

      const hasGraph = Boolean(response?.graph || response?.type === "graph");
      const graphData = response?.graph || response?.graph_data || response?.data;

      speak(botText);

      const thisBotId = botId; // capture correct id for this request

      startTypewriter(botText, () => {
        if (activeBotMsgIdRef.current !== thisBotId) return;

        const final = (isTypingStopped
          ? (fullBotTextRef.current || botText)
          : botText
        );

        dispatch({
          type: "CHAT_UPDATE_MESSAGE",
          data: {
            id: thisBotId,
            patch: { text: final, loading: false, hasGraph, graphData, type: hasGraph ? "graph" : "text" },
          },
        });
      });

    } catch (error) {
      console.error("Failed to send message:", error);

      const thisBotId = botId;
      dispatch({
        type: "CHAT_UPDATE_MESSAGE",
        data: {
          id: thisBotId,
          patch: {
            text: "Something went wrong. Please try again.",
            loading: false,
          },
        },
      });
    } finally {
      dispatch(setChatbotState({ loading: false }));
    }
  }, [
    inputValue,
    dispatch,
    stopSpeak,
    speak,
    startTypewriter,
    startRepeatingText,
    resetInactivity,
    listening,
    isTypingStopped,
    nextId,
  ]);

  /* ================= DRAG HANDLING ================= */
  const onMouseDown = useCallback(
    (e) => {
      if (fullscreenMode) return;

      e.preventDefault();
      didDrag.current = false;

      const onMouseMove = (ev) => {
        didDrag.current = true;

        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;

        const elemWidth = 60;
        const elemHeight = 60;

        let newX = ev.clientX - 30;
        let newY = ev.clientY - 30;

        if (newX < 0) newX = 0;
        if (newY < 0) newY = 0;
        if (newX + elemWidth > windowWidth) newX = windowWidth - elemWidth;
        if (newY + elemHeight > windowHeight) newY = windowHeight - elemHeight;

        dispatch(setChatbotState({ position: { x: newX, y: newY } }));
      };

      const onMouseUp = () => {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
      };

      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    },
    [dispatch, fullscreenMode]
  );

  /* ================= INPUT ================= */
  const handleInputChange = useCallback(
    (e) => {
      dispatch(setChatbotState({ inputValue: e.target.value }));
      resetInactivity();
    },
    [dispatch, resetInactivity]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const handleSpeech = useCallback(
    (text) => {
      dispatch(setChatbotState({ inputValue: text }));
      resetInactivity();
    },
    [dispatch, resetInactivity]
  );

  /* ================= STOP (show full message) ================= */
  const stopAll = useCallback(() => {
    dispatch(setChatbotState({ isTypingStopped: true, loading: false }));

    if (typewriterRef.current) {
      clearInterval(typewriterRef.current);
      typewriterRef.current = null;
    }
    if (repeatIntervalRef.current) {
      clearInterval(repeatIntervalRef.current);
      repeatIntervalRef.current = null;
    }

    window.speechSynthesis.cancel();
    speechRef.current = null;

    const finalText =
      fullBotTextRef.current?.trim() || currentBotText?.trim() || "Stopped.";

    const activeId = activeBotMsgIdRef.current;
    if (activeId) {
      dispatch({
        type: "CHAT_UPDATE_MESSAGE",
        data: { id: activeId, patch: { loading: false, text: finalText } },
      });
    }

    dispatch(setChatbotState({ currentBotText: "" }));
  }, [dispatch, currentBotText]);

  /* ================= FULLSCREEN ================= */
  const toggleFullscreen = useCallback(() => {
    dispatch(setChatbotState({ fullscreenMode: !fullscreenMode }));
    resetInactivity();
  }, [dispatch, fullscreenMode, resetInactivity]);

  if (!showChatbot) return null;

  return (
    <>
      {!isOpen && (
        <button
          className="aichatBot-button"
          style={{ left: `${position.x}px`, top: `${position.y}px` }}
          onMouseDown={onMouseDown}
          onClick={() =>
            !didDrag.current && dispatch(setChatbotState({ isOpen: true }))
          }
          aria-label="Open chat"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {isOpen && (
        <div
          className={`aichatBot-window ${fullscreenMode ? "fullscreen" : ""} ${isSidebarCollapsed ? "layout--collapse" : ""
            }`}
        >
          <div className="aichatBot-header">
            <div className="header-left">
              <h3>Lexato Support</h3>
              {timerEnabled && timeLeft > 0 && (
                <span className="timer">{timeLeft}s</span>
              )}
            </div>

            <div className="header-right">
              <button
                className="fullscreen-toggle-btn"
                onClick={toggleFullscreen}
                aria-label={fullscreenMode ? "Exit fullscreen" : "Enter fullscreen"}
                title={fullscreenMode ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {fullscreenMode ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
              </button>

              <button
                className="minimize-btn"
                onClick={closeChat}
                aria-label={fullscreenMode ? "Close chat" : "Minimize chat"}
                title={fullscreenMode ? "Close chat" : "Minimize chat"}
              >
                {fullscreenMode ? <X size={18} /> : "—"}
              </button>
            </div>
          </div>

          <div className="aichatBot-messages">
            {conversation.map((msg) => (
              <div
                key={msg.id}
                className={`message-container ${msg.sender === "user" ? "user-container" : "bot-container"
                  }`}
              >
                {msg.sender === "bot" && (
                  <>
                    <div className="avatar bot-avatar">
                      <Bot size={18} />
                    </div>

                    <div className="message-bubble bot-bubble">
                      {msg.loading ? (
                        <div className="message-content">
                          {currentBotText}
                          {!currentBotText && (
                            <div className="typing-indicator">
                              <span></span>
                              <span></span>
                              <span></span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <>
                          <div
                            className="message-content"
                            dangerouslySetInnerHTML={{ __html: marked(msg.text) }}
                          />
                          {msg.hasGraph && msg.graphData && renderGraph(msg.graphData)}
                        </>
                      )}
                    </div>
                  </>
                )}

                {msg.sender === "user" && (
                  <>
                    <div className="message-bubble user-bubble">
                      <div className="message-content" />
                      {msg.text}
                    </div>
                    <div className="avatar user-avatar">
                      <User size={18} />
                    </div>
                  </>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="aichatBot-input">
            <div className="textarea-container">
              <TextareaAutosize
                ref={inputRef}
                className={`textarea-upward ${loading ? "loading" : ""}`}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                minRows={1}
                maxRows={6}
                disabled={(loading && typewriterRef.current) || window.speechSynthesis.speaking || loading}
                placeholder="Type a message..."
                aria-label="Chat message input"
                spellCheck
                autoComplete="off"
              />

              <div className="speech-to-text-wrapper">
                <SpeechToTextButton
                  inputRef={inputRef}
                  handleSpeech={handleSpeech}
                  listening={listening}
                  setListening={(val) => dispatch(setChatbotState({ listening: val }))}
                  sendDataToServer={sendMessage}
                  transcriptRef={transcriptRef}
                  type="chatbot"
                  disabled={loading}
                />
              </div>
            </div>

            {(loading && typewriterRef.current) || window.speechSynthesis.speaking || loading ? (
              <FontAwesomeIcon
                icon={faStopCircle}
                size="3x"
                onClick={stopAll}
                style={{ color: "#bb0000", cursor: "pointer" }}
                aria-label="Stop Voice Input"
              />
            ) : (
              <button
                type="submit"
                className="send-btn"
                disabled={!inputValue.trim()}
                onClick={sendMessage}
                aria-label="Send message"
                title="Send message"
              >
                ➤
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
